import { useEffect, useState } from "react";

import { Box, Slider, TextField } from "@mui/material";

export interface NumberRange {
  start?: number;
  end?: number;
}

export interface NumberRangeSelectorProps {
  startValue?: number;
  endValue?: number;
  minValue: number;
  maxValue: number;
  disabled?: boolean;
  showLessThanPrefix?: boolean;
  maxLength?: number;
  onRangeChange: (range: NumberRange) => void;
}

const NumberRangeSelector = ({
  startValue,
  endValue,
  minValue,
  maxValue,
  disabled = false,
  showLessThanPrefix = false,
  maxLength = 4,
  onRangeChange
}: NumberRangeSelectorProps) => {
  // Local string state allows free typing; values are committed on blur
  const [startInput, setStartInput] = useState(startValue?.toString() ?? "");
  const [endInput, setEndInput] = useState(endValue?.toString() ?? "");

  // Sync local state when props change externally (e.g. slider drag or clear)
  useEffect(() => {
    setStartInput(startValue?.toString() ?? "");
  }, [startValue]);

  useEffect(() => {
    setEndInput(endValue?.toString() ?? "");
  }, [endValue]);

  const handleTextFieldChange = (
    type: "start" | "end",
    value: string
  ): void => {
    if (!/^\d*$/.test(value)) return;
    if (type === "start") setStartInput(value);
    else setEndInput(value);

    // Apply immediately if the value is already within valid bounds,
    // otherwise wait for blur to clamp it
    if (value === "") {
      onRangeChange({
        start: type === "start" ? undefined : startValue,
        end: type === "end" ? undefined : endValue
      });
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (parsed >= minValue && parsed <= maxValue) {
      if (type === "start") {
        onRangeChange({ start: parsed, end: endValue });
      } else {
        onRangeChange({ start: startValue, end: parsed });
      }
    }
  };

  const handleBlur = (type: "start" | "end"): void => {
    const raw = type === "start" ? startInput : endInput;

    if (raw === "") {
      onRangeChange({
        start: type === "start" ? undefined : startValue,
        end: type === "end" ? undefined : endValue
      });
      return;
    }

    const parsed = Number.parseInt(raw, 10);
    const clamped = Math.min(Math.max(parsed, minValue), maxValue);

    if (type === "start") {
      const clampedStart =
        endValue === undefined ? clamped : Math.min(clamped, endValue);
      onRangeChange({ start: clampedStart, end: endValue });
      setStartInput(clampedStart.toString());
    } else {
      const clampedEnd =
        startValue === undefined ? clamped : Math.max(clamped, startValue);
      onRangeChange({ start: startValue, end: clampedEnd });
      setEndInput(clampedEnd.toString());
    }
  };

  const handleSliderChange = (
    _event: Event,
    newValue: number | number[]
  ): void => {
    const [start, end] = newValue as number[];
    onRangeChange({ start, end });
  };

  const textFieldWidth = "100px";

  // Ensure start value is never less than minValue
  const safeStartValue =
    startValue !== undefined ? Math.max(startValue, minValue) : minValue;

  return (
    <Box sx={{ px: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <TextField
          size="small"
          value={startInput}
          disabled={disabled}
          sx={{ width: textFieldWidth }}
          onChange={(e) => handleTextFieldChange("start", e.target.value)}
          onBlur={() => handleBlur("start")}
          slotProps={{
            htmlInput: {
              maxLength,
              inputMode: "numeric",
              pattern: "[0-9]*"
            }
          }}
        />

        <TextField
          size="small"
          value={endInput}
          disabled={disabled}
          sx={{ width: textFieldWidth }}
          onChange={(e) => handleTextFieldChange("end", e.target.value)}
          onBlur={() => handleBlur("end")}
          slotProps={{
            htmlInput: {
              maxLength,
              inputMode: "numeric",
              pattern: "[0-9]*"
            }
          }}
        />
      </Box>
      <Slider
        sx={{
          my: 1,
          "& .MuiSlider-markLabel[data-index='1']": {
            pr: 1
          },
          "& .MuiSlider-markLabel[data-index='0']": {
            pl: 2
          }
        }}
        disabled={disabled}
        getAriaLabel={() => "number-range-selector"}
        value={[safeStartValue, endValue ?? maxValue]}
        onChange={handleSliderChange}
        valueLabelDisplay="auto"
        min={minValue}
        max={maxValue}
        marks={[
          {
            value: minValue,
            label: showLessThanPrefix ? `< ${minValue}` : minValue
          },
          {
            value: maxValue,
            label: maxValue
          }
        ]}
      />
    </Box>
  );
};

export default NumberRangeSelector;
